import type { AiSearchRefusalReason } from './ai-search-analytics';

/**
 * Tiny heuristic that maps a `useChat` error or an assistant turn that
 * produced no tool call into a coarse `block_reason` bucket.
 *
 * V1 keeps the taxonomy small (`'safety' | 'off_topic' | 'unknown'`) — a
 * server-side classifier can refine this later. The signal we care about
 * for now is "did the AI search funnel break, and roughly why?".
 *
 * Pure / framework-free so it can be unit-tested without a React tree.
 */
export function classifyRefusalFromError(error: unknown): AiSearchRefusalReason {
    if (!error) return 'unknown';
    const message = extractMessage(error).toLowerCase();
    if (message.length === 0) return 'unknown';

    if (
        message.includes('safety') ||
        message.includes('content_policy') ||
        message.includes('content policy') ||
        message.includes('moderation') ||
        message.includes('blocked')
    ) {
        return 'safety';
    }

    return 'unknown';
}

/**
 * Map a finished assistant turn that produced NO tool call into a refusal
 * reason. Callers should only invoke this once they've confirmed the turn
 * has no `tool-search_activities` part (see `selectToolResult`).
 */
export function classifyRefusalFromAssistantText(text: string): AiSearchRefusalReason {
    const normalized = text.trim().toLowerCase();
    if (normalized.length === 0) return 'unknown';

    if (
        normalized.includes("can't help") ||
        normalized.includes('cannot help') ||
        normalized.includes("can't assist") ||
        normalized.includes('cannot assist') ||
        normalized.includes('not able to') ||
        normalized.includes('against my') ||
        normalized.includes('unable to provide')
    ) {
        return 'safety';
    }

    // Anything else where the model talked but didn't call the tool is treated
    // as the model going off-topic relative to activity search.
    return 'off_topic';
}

function extractMessage(error: unknown): string {
    if (typeof error === 'string') return error;
    if (error instanceof Error) return error.message;
    if (typeof error === 'object' && error !== null && 'message' in error) {
        const m = error.message;
        if (typeof m === 'string') return m;
    }
    return '';
}
